//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { ColumnModel } from '../../../api/api.models'

import { CreateEditInitParams } from '../../../common/create-edit-popup/create-edit-popup.controller'
import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../../common/common.details'
import { DetailsParametersOptions } from '../../../common/details-parameters/details-parameters.directive'
import { DetailsPopupOptions, DetailsOptions } from '../../../common/details-popup/details-popup.directive'

import { HistorySource } from '../../../common/history'
import { GridDataSourceType, GridOptions, GridTypes, GridController } from '../../../common/grid'

import {
    ConfigHelper, EventsNames,
    OperatorLayoutSettings, LocalizationHelper, TemplateHelper
} from '../../../../helpers/'

import { Activity, Enums } from '../../../../models/common/'
import { OpDataTypeInGroup, OpDepositoryElement, OpPackage } from '../../../../models/core'

import { OrderService, OrderContentService, OrderRelatedPackagesService, OrderCompleteService } from '../../../../services/order'
import { UserSessionService } from '../../../../services/user-session'

import { CompleteOrderOptions, ICompleteOrder } from '../complete-order'

interface OrderDetailsScope extends DetailsScope {
    id: number
}

export class OrderDirectiveController extends Details<OrderDetailsScope> {

    //#region Consts

    public get ORDER_COMMON_TAB_NAME() { return 'ORDER_COMMON_TAB_NAME' }
    public get ORDER_HISTORY_TAB_NAME() { return 'ORDER_HISTORY_TAB_NAME' }
    public get ORDER_PARAMS_ACCORDION_NAME() { return 'ORDER_PARAMS_ACCORDION_NAME' }
    public get ORDER_RELATED_PACKAGES_ACCORDION_NAME() { return 'ORDER_RELATEDPACKAGES_ACCORDION_NAME' }
    public get ORDER_CONTENT_ACCORDION_NAME() { return 'ORDER_CONTENT_ACCORDION_NAME' }

    private get ORDER_CONTENT_GRID_NAME(): string { return 'ORDER_DETAILS_CONTENT_GRID_NAME' }
    private get RELATED_PACKAGES_GRID_NAME(): string { return 'ORDER_DETAILS_RELATED_PACKAGES_GRID_NAME' }
    private get ORDER_HISTORY_NAME(): string { return 'ORDER_HISTORY_NAME' }

    //#endregion


    //#region Members

    public completeOrderOptions: CompleteOrderOptions
    public detailsParametersOptions: DetailsParametersOptions
    public orderContentGridOptions: GridOptions
    public orderHistoryOptions: HistorySource
    public relatedPackagesGridOptions: GridOptions
    public deleteConfirmationOptions: {
        title: string
        onConfirmed: () => void
    }

    private completeActionButton: DevExpress.ui.dxButton
    private completeOrder: ICompleteOrder
    private order: OpPackage
    private idOrder: number
    private orderContentGrid: GridController<OpDepositoryElement>
    private relatedPackagesGrid: GridController<OpPackage>

    //#endregion


    //#region Ctor

    constructor(
        $scope: OrderDetailsScope,
        $timeout: ng.ITimeoutService,
        private orderService: OrderService,
        private orderCompleteService: OrderCompleteService,
        private orderContentService: OrderContentService,
        private orderRelatedPackagesService: OrderRelatedPackagesService,
        protected userSessionService: UserSessionService,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, null, null, null, OperatorLayoutSettings.OrderPopupTabActive, userSessionService, referenceType)

        this.setConfirmationsOptions()
        this.setEvents()
        this.setOptions()

        if (this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    //#endregion


    //#region Events

    private onOpen(options: DetailsOptions): void {
        this.onClear()

        this.$scope.detailsOpenParams = options

        this.idOrder = options.referenceObjectId

        this.onRefresh()
        this.showPopup()
    }

    private onRefresh(toRefresh: OpPackage = null): void {
        if (!toRefresh) {
            this.orderService
                .getOrder(this.idOrder)
                .then((order: OpPackage) => {
                    this.initialize(order)
                })
                .fail(() => {
                    this.hidePopup()
                })
        } else {
            this.initialize(toRefresh)
        }
    }

    private onClear(): void {
        this.$scope.popupTitle = ''
        this.$scope.showTabs = false

        this.order = null
        this.idOrder = null
        this.detailsParametersOptions = null

        this.relatedPackagesGrid = null
        this.relatedPackagesGridOptions = null

        this.orderContentGrid = null
        this.orderContentGridOptions = null
    }

    private onClose(): void {
        this.hidePopup()
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.detailsPopupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onConfirmDelete(): void {
        this.orderService
            .deleteOrder(this.order)
            .then(() => {
                this.onClose()
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
            })
    }

    // #endregion


    //#region Initialize

    private initialize(order: OpPackage): void {
        if (order != null && (!this.order || this.order.idPackage == order.idPackage)) {
            OpDepositoryElement
                .loadNavigationProperties(this.userSessionService.getDataProvider(), order.contentList)
                .then(() => {
                    this.order = order
                    this.$scope.popupTitle = order.toString()

                    this.initializeBasicParameters()
                    this.initializeCompleteOrder(order)
                    this.initializeContent(order.contentList)
                    this.initializeHistoryOptions(order)
                    this.initializeRelatedPackages(order.relatedPackageList)

                    this.$scope.showTabs = true
                })
        }
    }

    //#endregion


    //#region Actions

    private getActions(): DevExpress.ui.dxButtonOptions[] {
        const actions: DevExpress.ui.dxButtonOptions[] = []

        actions.push({
            disabled: true,

            template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CompleteOrder), ConfigHelper.Icon.ORDER, this.userSessionService),
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CompleteOrder),

            onInitialized: (e: any) => {
                this.completeActionButton = e.component

                this.updateCompleteActionButton()
            },

            onClick: (e: any) => {
                this.openCompleteOrder()
            }
        })

        return actions
    }

    //#endregion


    //#region Complete Order

    private initializeCompleteOrder(order: OpPackage): void {
        this.updateCompleteActionButton()
        this.initializeCompleteOrderPopup()
    }

    private initializeCompleteOrderPopup(): void {
        this.completeOrderOptions = {
            onInitialized: (component: ICompleteOrder) => {
                this.completeOrder = component
            },

            onOrderCompleted: () => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CONFIRMED, this.moduleName))

                this.onRefresh()
            }
        }
    }

    private openCompleteOrder(): void {
        this.completeOrder.complete([this.order])
    }

    private updateCompleteActionButton() {
        if (this.completeActionButton != null) {
            this.completeActionButton.option('disabled', !this.orderCompleteService.isOrderCompletable(this.order))
        }
    }

    //#endregion


    //#region Content

    private initializeContent(contentList: OpDepositoryElement[]): void {
        this.orderContentGridOptions = null

        this.$timeout(() => {
            this.orderContentGridOptions = {
                addCustomFilters: true,
                dataSourceType: GridDataSourceType.Properties,
                columnsDictionary: this.orderContentService.getOrderContentColumns(),
                dataSource: contentList,
                name: this.ORDER_CONTENT_GRID_NAME,
                type: GridTypes.PAGINATED_SMALL,

                allowColumnReordering: !this.userSessionService
                    .getLoggedOperator()
                    .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OrderContent),

                buttonsOptions: {
                    enabled: true
                },

                onInitialized: (e: any, grid: GridController<OpDepositoryElement>) => {
                    this.orderContentGrid = grid
                },
            }
        }, 100)
    }

    private onEditOrderContent(): void {
        this.orderService
            .getCreateEditOptions(this.order.idPackage, this.order)
            .then((createEditInitParams: CreateEditInitParams) => {
                createEditInitParams.createEditSelectedTabType = Enums.SimaxCreateEditSelectedTabType.ExtendedParameters

                this.$scope.$root.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
            })
    }

    //#endregion


    //#region History

    private initializeHistoryOptions(order: OpPackage): void {
        this.orderHistoryOptions = {
            dataMethodName: null,
            id: null,
            name: this.ORDER_HISTORY_NAME,
            referenceType: this.referenceType,
            referenceValue: order.idPackage,

            allowColumnReordering: !this.userSessionService
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OrderHistory),
        }
    }

    //#endregion


    //#region Related Packages

    private initializeRelatedPackages(packages: OpPackage[]): void {
        if (this.relatedPackagesGrid != null) {
            this.relatedPackagesGrid.setDataSource(packages)
        } else {
            this.orderRelatedPackagesService
                .getRelatedPackagesColumns()
                .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                    this.relatedPackagesGridOptions = {
                        addCustomFilters: true,
                        columnsDictionary: columns,
                        defaultColumnsDictionary: defaultColumns,
                        dataSource: packages,
                        idReferenceType: this.referenceType,
                        name: this.RELATED_PACKAGES_GRID_NAME,
                        type: GridTypes.PAGINATED_SMALL,

                        allowColumnReordering: !this.userSessionService
                            .getLoggedOperator()
                            .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OrderAssociatedPackages),

                        buttonsOptions: {
                            enabled: true
                        },

                        onInitialized: (e: any, grid: GridController<OpPackage>) => {
                            this.relatedPackagesGrid = grid
                        },

                        onRowDoubleClick: () => {
                            this.openRelatedPackageDetails(this.relatedPackagesGrid.selectedItem)
                        }
                    }
                })
        }
    }

    private openRelatedPackageDetails(relatedPackage: OpPackage) {
        if (this.userSessionService.getLoggedOperator().hasPermission(Activity.PACKAGE_LIST)) {
            this.orderRelatedPackagesService.openRelatedPackageDetails(relatedPackage.idPackage)
        }
    }

    //#endregion


    //#region Parameters

    private initializeBasicParameters(): void {
        this.orderService
            .getBasicParametrsDataTypeGroup()
            .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                this.detailsParametersOptions = {
                    dataTypes: dataTypeInGroup,
                    referenceType: this.referenceType,
                    referenceObject: this.order,
                    useDynamicValues: true
                }
            })
    }

    private onEditParams(): void {
        this.orderService
            .getCreateEditOptions(this.order.idPackage, this.order)
            .then((createEditInitParams: CreateEditInitParams) => {
                this.$scope.$root.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
            })
    }

    //#endregion


    //#region Setup

    public getAccordions(): any[] {
        const accordions: DetailsAccordion[] = []
        let items: DetailsAccordionSection[] = []

        if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.OrderParameters)
            || !this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.OrderAssociatedPackages)
        ) {
            items = []

            if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.OrderParameters)) {
                items.push({
                    name: this.ORDER_PARAMS_ACCORDION_NAME,
                    showInTab: this.ORDER_COMMON_TAB_NAME,
                    showStatus: false,
                    title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Parameters),
                    visible: true,
                    canEdit: this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_EDIT)
                        && !this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.OrderParameters),

                    editMethod: () => this.onEditParams(),
                })
            }

            if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.OrderAssociatedPackages)) {
                items.push({
                    name: this.ORDER_RELATED_PACKAGES_ACCORDION_NAME,
                    showInTab: this.ORDER_COMMON_TAB_NAME,
                    showStatus: true,
                    title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RelatedPackages),
                    visible: true,
                    canEdit: false
                })
            }

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.OrderDetailsAccordions, 0, items, true, (component: DevExpress.ui.dxAccordion) => {
                this.$timeout(() => {
                    component.expandItem(1)
                })
            }))
        }

        if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.OrderContent)) {
            items = []

            items.push({
                name: this.ORDER_CONTENT_ACCORDION_NAME,
                showInTab: this.ORDER_COMMON_TAB_NAME,
                showStatus: true,
                title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONTENT, this.moduleName),
                visible: true,
                canEdit: this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_EDIT)
                    && !this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.OrderContent),

                editMethod: () => {
                    this.onEditOrderContent()
                },

                status: () => {
                    return `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignedCount)}${this.order != null ? _.size(this.order.contentList) : 0}`
                }
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.OrderDetailsAccordions, 1, items))
        }

        return accordions
    }

    public getTabs(): any[] {
        const tabs: any[] = []

        if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.OrderParameters)
            || !this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.OrderAssociatedPackages)
            || !this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.OrderContent)
        ) {
            tabs.push({
                template: TemplateHelper.getContextMenuItem(
                    LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common),
                    ConfigHelper.Icon.HOME,
                    this.userSessionService
                ),
                name: this.ORDER_COMMON_TAB_NAME
            })
        }

        if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.OrderHistory)) {
            tabs.push({
                template: TemplateHelper.getContextMenuItem(
                    LocalizationHelper.getLocalizedText(LocalizationHelper.Text.History),
                    ConfigHelper.Icon.HISTORY,
                    this.userSessionService
                ),
                name: this.ORDER_HISTORY_TAB_NAME
            })
        }

        return tabs
    }

    private setConfirmationsOptions(): void {
        this.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => this.onOpen(args[3])
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.onRefresh(args[0]))
    }

    private setOptions(): void {
        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,

            actions: this.getActions(),
            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),
            delHasPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DEL) ? Activity[Activity.ORDER_DEL] : null,
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),

            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }

    //#endregion

    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}